import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft, 
  Search, 
  Loader2,
  FileCheck,
  RefreshCw,
  Play,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GRIRReconciliation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('CC001');
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [runningClearing, setRunningClearing] = useState(false);
  const [clearingResult, setClearingResult] = useState(null);

  useEffect(() => {
    loadCompanyCodes();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadReport();
    }
  // eslint-disable-next-line
  }, [selectedCompany, asOfDate]);

  const loadCompanyCodes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company/company-codes`);
      setCompanyCodes(response.data || []);
    } catch (error) {
      console.error('Error loading company codes:', error);
    }
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (asOfDate) params.append('as_of_date', asOfDate);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/grir-reconciliation/${selectedCompany}?${params}`
      );
      setReport(response.data);
    } catch (error) {
      console.error('Error loading reconciliation report:', error);
      toast.error('Failed to load reconciliation report');
    } finally {
      setLoading(false);
    }
  };

  const handleRunClearing = async () => {
    try {
      setRunningClearing(true);
      setClearingResult(null);
      
      const response = await axios.post(`${BACKEND_URL}/api/grir-auto-clearing`, {
        company_code: selectedCompany,
        clearing_date: new Date().toISOString().split('T')[0],
        tolerance_amount: 0.01,
        write_off_limit: 1.00,
        created_by: 'ADMIN'
      });
      
      setClearingResult(response.data);
      toast.success(`Clearing completed! ${response.data.items_cleared} items cleared.`);
      loadReport(); // Refresh report
    } catch (error) {
      console.error('Error running clearing:', error);
      toast.error('Failed to run auto-clearing');
    } finally {
      setRunningClearing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num || 0);
  };

  const getVarianceClass = (variance) => {
    if (variance === 0) return 'text-green-600';
    if (Math.abs(variance) < 1) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/inventory')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">GR/IR Reconciliation</h1>
              <p className="text-sm text-gray-500">MR11 - GR/IR Clearing Account Maintenance</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadReport} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button 
              onClick={handleRunClearing} 
              disabled={runningClearing || !report?.open_items_count}
              className="bg-green-600 hover:bg-green-700"
            >
              {runningClearing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running...</>
              ) : (
                <><Play className="h-4 w-4 mr-2" /> Run Auto-Clearing</>
              )}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Company Code</label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
              >
                {companyCodes.map(cc => (
                  <option key={cc.company_code_id} value={cc.company_code_id}>
                    {cc.company_code_id} - {cc.company_code_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">As Of Date</label>
              <Input
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Clearing Result */}
        {clearingResult && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-800">Auto-Clearing Completed</h3>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-green-600">Items Processed:</span>
                    <p className="font-medium">{clearingResult.items_processed}</p>
                  </div>
                  <div>
                    <span className="text-green-600">Items Cleared:</span>
                    <p className="font-medium">{clearingResult.items_cleared}</p>
                  </div>
                  <div>
                    <span className="text-green-600">Amount Cleared:</span>
                    <p className="font-medium">{formatCurrency(clearingResult.total_cleared_amount)}</p>
                  </div>
                  <div>
                    <span className="text-green-600">Clearing Docs:</span>
                    <p className="font-medium">{clearingResult.clearing_documents?.join(', ') || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {report && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
              <p className="text-sm text-gray-500">Open Items</p>
              <p className="text-2xl font-bold text-gray-900">{report.open_items_count}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
              <p className="text-sm text-gray-500">Total GR Amount</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(report.total_open_gr_amount)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
              <p className="text-sm text-gray-500">Total IR Amount</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(report.total_open_ir_amount)}</p>
            </div>
            <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${
              report.total_variance === 0 ? 'border-green-500' : 'border-red-500'
            }`}>
              <p className="text-sm text-gray-500">Total Variance</p>
              <p className={`text-xl font-bold ${getVarianceClass(report.total_variance)}`}>
                {formatCurrency(report.total_variance)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
              <p className="text-sm text-gray-500">Over 90 Days</p>
              <p className="text-xl font-bold text-orange-600">{formatCurrency(report.over_90_days)}</p>
            </div>
          </div>
        )}

        {/* Aging Summary */}
        {report && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Aging Analysis</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-3 bg-green-50 rounded">
                <p className="text-sm text-gray-600">Current (0-30)</p>
                <p className="text-lg font-bold text-green-700">{formatCurrency(report.current)}</p>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded">
                <p className="text-sm text-gray-600">31-60 Days</p>
                <p className="text-lg font-bold text-yellow-700">{formatCurrency(report.days_31_60)}</p>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded">
                <p className="text-sm text-gray-600">61-90 Days</p>
                <p className="text-lg font-bold text-orange-700">{formatCurrency(report.days_61_90)}</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded">
                <p className="text-sm text-gray-600">Over 90 Days</p>
                <p className="text-lg font-bold text-red-700">{formatCurrency(report.over_90_days)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Items Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
              <span className="ml-2 text-gray-600">Loading reconciliation data...</span>
            </div>
          ) : !report?.items?.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <CheckCircle2 className="h-12 w-12 mb-4 text-green-500" />
              <p className="text-green-600 font-medium">All items are cleared!</p>
              <p className="text-sm">No open GR/IR items found.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>PO Number</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Material</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">GR Qty</TableHead>
                  <TableHead className="text-right">GR Amount</TableHead>
                  <TableHead className="text-right">IR Qty</TableHead>
                  <TableHead className="text-right">IR Amount</TableHead>
                  <TableHead className="text-right">Qty Var</TableHead>
                  <TableHead className="text-right">Amt Var</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.items.map((item, index) => (
                  <TableRow key={index} className="hover:bg-gray-50">
                    <TableCell className="font-medium text-orange-600">{item.po_number}</TableCell>
                    <TableCell>{item.po_item}</TableCell>
                    <TableCell>
                      <div>
                        <span className="font-medium">{item.material_id || '-'}</span>
                        {item.material_description && (
                          <p className="text-xs text-gray-500 truncate max-w-[150px]">
                            {item.material_description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <span>{item.vendor_id}</span>
                        {item.vendor_name && (
                          <p className="text-xs text-gray-500">{item.vendor_name}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(item.gr_quantity)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.gr_amount)}</TableCell>
                    <TableCell className="text-right">{formatNumber(item.ir_quantity)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.ir_amount)}</TableCell>
                    <TableCell className={`text-right font-medium ${getVarianceClass(item.quantity_variance)}`}>
                      {formatNumber(item.quantity_variance)}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${getVarianceClass(item.amount_variance)}`}>
                      {formatCurrency(item.amount_variance)}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.clearing_status === 'CLEARED' ? 'bg-green-100 text-green-800' :
                        item.clearing_status === 'PARTIAL' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.clearing_status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GRIRReconciliation;
